import { useState, useRef, useEffect, useCallback } from "react";
import FloorToolbar from "../../toolbars/FloorToolbar";
import { Stage, Layer, Line, Image as KonvaImage, Circle, Rect } from "react-konva";
import useImage from "use-image";
import PlayerArrow from "../../utilities/PlayerArrow";
import UtilityIcon from "../../utilities/UtilityIcon";
import PopoutIconGrid from "../../utilities/PopoutIconGrid";
import UtilityIconPopoutGrid from "../../utilities/UtilityIconPopoutGrid";
import { getCurrentFloorCalloutSrc, getCurrentFloorLayerSrc } from "./callouts";


const R6sWhiteboard = ({
  activeTool = "move",
  selectedShape = null,
  clearDrawings,
  registerUndo,
  registerRedo,
  registerExport,
  playCommand,
  onPlaybackFinished,
  game = "r6s",
  backgroundUtility = null,
  selectedMap,
  onMapChange,
  calloutsVisible = false,
  camsVisible = false,
  textUtility = null,
  drawingColor = "#ff0000",
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [popoutAnchor, setPopoutAnchor] = useState({ x: 0, y: 0 });
  const [popoutPlayerId, setPopoutPlayerId] = useState(null);
  // badge number popout
  const [badgePopOpen, setBadgePopOpen] = useState(false);
  const [badgePopAnchor, setBadgePopAnchor] = useState({ x: 0, y: 0 });
  const [badgeTargetId, setBadgeTargetId] = useState(null);
  const [lines, setLines] = useState([]);
  const [shapes, setShapes] = useState([]);
  const isDrawing = useRef(false);
  const isDrawingShape = useRef(false);
  const shapeDrawingStart = useRef(null);
  const stageRef = useRef(null);
  const stageContainerRef = useRef(null);
  const [routes, setRoutes] = useState([]);
  const routesRef = useRef([]);
  const [stageSize, setStageSize] = useState({
    width: 0,
    height: 0,
  });
  const [siteImageVisible, setSiteImageVisible] = useState(false);
  const [siteImageSrc, setSiteImageSrc] = useState(null);

  const [players, setPlayers] = useState([
    { id: 1, x: 400, y: 200, rotation: 0, img: "/icons/r6s/operators/recruit_blue.png", strokeColor: 'blue' },
    { id: 2, x: 400, y: 400, rotation: 0, img: "/icons/r6s/operators/recruit_green.png", strokeColor: 'green' },
    { id: 3, x: 400, y: 600, rotation: 0, img: "/icons/r6s/operators/recruit_orange.png", strokeColor: 'orange' },
    { id: 4, x: 400, y: 800, rotation: 0, img: "/icons/r6s/operators/recruit_red.png", strokeColor: 'red' },
    { id: 5, x: 400, y: 700, rotation: 0, img: "/icons/r6s/operators/recruit_yellow.png", strokeColor: 'yellow' },
    { id: 6, x: 1520, y: 200, rotation: 0, img: "/icons/r6s/operators/recruit_blue.png", strokeColor: 'blue' },
    { id: 7, x: 1520, y: 400, rotation: 0, img: "/icons/r6s/operators/recruit_green.png", strokeColor: 'green' },
    { id: 8, x: 1520, y: 600, rotation: 0, img: "/icons/r6s/operators/recruit_orange.png", strokeColor: 'orange' },
    { id: 9, x: 1520, y: 800, rotation: 0, img: "/icons/r6s/operators/recruit_red.png", strokeColor: 'red' },
    { id: 10, x: 1520, y: 700, rotation: 0, img: "/icons/r6s/operators/recruit_yellow.png", strokeColor: 'yellow' },
  ]);

  // selectedMap is the ID string; currentMap holds the full object from JSON
  const [currentMap, setCurrentMap] = useState(null);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  // Store per-floor whiteboard state: { [floorId]: { lines, players, shapes, utilities, routes } }
  const [floorStates, setFloorStates] = useState({});
  const [utilities, setUtilities] = useState([]);
  const [backgroundImageVisible, setBackgroundImageVisible] = useState(true);
  const [calloutImageVisible, setCalloutImageVisible] = useState(false);
  const [calloutImageSrc, setCalloutImageSrc] = useState(null);
  const [camImageVisible, setCamImageVisible] = useState(false);
  const [camImageSrc, setCamImageSrc] = useState(null);
  // Get current floor object
  const currentFloor = currentMap && currentMap.floors && selectedFloorId
    ? currentMap.floors.find(f => f.id === selectedFloorId)
    : null;

  // Use floor-specific background and wireframe images
  const [backgroundImage] = useImage(
    backgroundImageVisible && currentFloor ? currentFloor.bgSrc : null
  );
  const [mapImage] = useImage(currentFloor ? currentFloor.wireframeSrc : null);
  const [calloutImage] = useImage(calloutImageVisible ? calloutImageSrc : null);
  const [camImage] = useImage(camImageVisible ? camImageSrc : null);
  const [siteImage] = useImage(siteImageVisible ? siteImageSrc : null);

  useEffect(() => {
    setBackgroundImageVisible(!!backgroundUtility && backgroundUtility.id === "background");
  }, [backgroundUtility]);

  // When map changes, set default floor
  useEffect(() => {
    if (currentMap && currentMap.floors && currentMap.floors.length > 0) {
      setSelectedFloorId(currentMap.floors[0].id);
    }
  }, [currentMap]);

  // react to text utility (site) selection and show the site image overlay
  useEffect(() => {
    if (!textUtility) {
      setSiteImageVisible(false);
      setSiteImageSrc(null);
      return;
    }

    const src = textUtility.imageSrc || textUtility.image || null;
    if (src) {
      setSiteImageSrc(src);
      setSiteImageVisible(true);
    } else {
      setSiteImageVisible(false);
      setSiteImageSrc(null);
    }
  }, [textUtility]);

  // Show/hide site image when textUtility selection changes
  useEffect(() => {
    if (!backgroundUtility && !selectedMap) return; // nothing meaningful without map
  }, [backgroundUtility, selectedMap]);


  useEffect(() => {
    if (!currentMap || !currentFloor) {
      setCalloutImageSrc(null);
      setCalloutImageVisible(false);
      setCamImageSrc(null);
      setCamImageVisible(false);
      return;
    }

    const nextCalloutSrc = getCurrentFloorCalloutSrc(currentMap, currentFloor);
    setCalloutImageSrc(nextCalloutSrc);
    setCalloutImageVisible(Boolean(calloutsVisible && nextCalloutSrc));

    const nextCamSrc = getCurrentFloorLayerSrc(currentMap, currentFloor, 'cams');
    setCamImageSrc(nextCamSrc);
    setCamImageVisible(Boolean(camsVisible && nextCamSrc));
  }, [currentMap, currentFloor, calloutsVisible, camsVisible]);



  // load map definitions and then the full map object (with floors) from per-map JSON
  useEffect(() => {
    if (!selectedMap) return;
    let mounted = true;
    const loadMap = async () => {
      try {
        const data = (await import(`../../data/maps/${game}/maps.json`)).default;
        const allMaps = Array.isArray(data.maps) ? data.maps : [];
        const found = allMaps.find((item) => item.id === selectedMap);
        if (found && found.basemap) {
          // Load the full map JSON (e.g., chalet.json)
          const mapJson = (await import(`../../data/maps/${game}/${found.basemap}.json`)).default;
          if (mounted) {
            setCurrentMap(mapJson);
            if (typeof onMapChange === 'function') onMapChange(mapJson);
          }
        } else {
          if (mounted) {
            setCurrentMap(found || null);
            if (typeof onMapChange === 'function') onMapChange(found || null);
          }
        }
      } catch (err) {
        console.error("Failed to load map data", err);
        if (mounted) {
          setCurrentMap(null);
          if (typeof onMapChange === 'function') onMapChange(null);
        }
      }
    };
    loadMap();
    return () => {
      mounted = false;
    };
  }, [selectedMap, game, onMapChange]);

  // Save current floor state before switching, restore new floor state after switching
  const saveCurrentFloorState = useCallback(() => {
    if (!selectedFloorId) return;
    setFloorStates(prev => ({
      ...prev,
      [selectedFloorId]: {
        lines: JSON.parse(JSON.stringify(lines)),
        players: JSON.parse(JSON.stringify(players)),
        shapes: JSON.parse(JSON.stringify(shapes)),
        utilities: JSON.parse(JSON.stringify(utilities)),
        routes: JSON.parse(JSON.stringify(routes)),
      }
    }));
  }, [selectedFloorId, lines, players, shapes, utilities, routes]);

  const restoreFloorState = useCallback((floorId) => {
    const state = floorStates[floorId];
    if (state) {
      setLines(state.lines || []);
      setPlayers(state.players || []);
      setShapes(state.shapes || []);
      setUtilities(state.utilities || []);
      setRoutes(state.routes || []);
    } else {
      setLines([]);
      setPlayers([
        { id: 1, x: 400, y: 200, rotation: 0, img: "/icons/r6s/operators/recruit_blue.png", strokeColor: 'blue' },
        { id: 2, x: 400, y: 400, rotation: 0, img: "/icons/r6s/operators/recruit_green.png", strokeColor: 'green' },
        { id: 3, x: 400, y: 600, rotation: 0, img: "/icons/r6s/operators/recruit_orange.png", strokeColor: 'orange' },
        { id: 4, x: 400, y: 800, rotation: 0, img: "/icons/r6s/operators/recruit_red.png", strokeColor: 'red' },
        { id: 5, x: 400, y: 700, rotation: 0, img: "/icons/r6s/operators/recruit_yellow.png", strokeColor: 'yellow' },
        { id: 6, x: 1520, y: 200, rotation: 0, img: "/icons/r6s/operators/recruit_blue.png", strokeColor: 'blue' },
        { id: 7, x: 1520, y: 400, rotation: 0, img: "/icons/r6s/operators/recruit_green.png", strokeColor: 'green' },
        { id: 8, x: 1520, y: 600, rotation: 0, img: "/icons/r6s/operators/recruit_orange.png", strokeColor: 'orange' },
        { id: 9, x: 1520, y: 800, rotation: 0, img: "/icons/r6s/operators/recruit_red.png", strokeColor: 'red' },
        { id: 10, x: 1520, y: 700, rotation: 0, img: "/icons/r6s/operators/recruit_yellow.png", strokeColor: 'yellow' },
      ]);
      setShapes([]);
      setUtilities([]);
      setRoutes([]);
    }
  }, [floorStates]);

  // When selectedFloorId changes, save old and restore new
  useEffect(() => {
    // skip on first mount
    if (!selectedFloorId) return;
    restoreFloorState(selectedFloorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloorId]);

  // Handler for toolbar button click
  const handleSelectFloor = (floorId) => {
    saveCurrentFloorState();
    setSelectedFloorId(floorId);
  };

  // Use per-floor mapImage (already declared above)
  const imgW = mapImage?.width || 1920;
  const imgH = mapImage?.height || 1080;
  const scale = Math.min(stageSize.width / imgW, stageSize.height / imgH);
  const imgDisplayW = imgW * scale;
  const imgDisplayH = imgH * scale;
  const offsetX = (stageSize.width - imgDisplayW) / 2;
  const offsetY = (stageSize.height - imgDisplayH) / 2;

  const stageToImage = useCallback(
    (pt) => ({
      x: (pt.x - offsetX) / scale,
      y: (pt.y - offsetY) / scale,
    }),
    [offsetX, offsetY, scale]
  );

  const imageToStagePoints = (points) =>
    points.map((p, i) => (i % 2 === 0 ? p * scale + offsetX : p * scale + offsetY));

  // Eraser state 
  const isErasing = useRef(false);
  const [eraserPos, setEraserPos] = useState(null);
  const ERASER_RADIUS = 16;
  const DENSIFY_THRESHOLD = 4; // px in image-space - max distance between points when drawing

  // Undo/redo stacks (store snapshots of lines + players)
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const MAX_HISTORY = 100;

  // refs to always access latest state inside undo/redo functions
  const linesRef = useRef(lines);
  const playersRef = useRef([]);
  const shapesRef = useRef([]);
  const utilitiesRef = useRef([]);
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);
  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);
  useEffect(() => {
    utilitiesRef.current = utilities;
  }, [utilities]);

  const snapshotState = useCallback(() => ({
    lines: JSON.parse(JSON.stringify(linesRef.current || [])),
    players: JSON.parse(JSON.stringify(playersRef.current || [])),
    routes: JSON.parse(JSON.stringify(routesRef.current || [])),
    shapes: JSON.parse(JSON.stringify(shapesRef.current || [])),
    utilities: JSON.parse(JSON.stringify(utilitiesRef.current || [])),
  }), []);
  
  const pushUndo = useCallback(() => {
    // avoid pushing duplicate consecutive snapshots
    const snap = snapshotState();
    const last = undoStack.current[undoStack.current.length - 1];
    if (last && JSON.stringify(last) === JSON.stringify(snap)) return;
    undoStack.current.push(snap);
    if (undoStack.current.length > MAX_HISTORY) undoStack.current.shift();
    // clear redo on new action
    redoStack.current = [];
  }, [snapshotState]);
  
  const applySnapshot = (snap) => {
    setLines(snap.lines || []);
    setPlayers(snap.players || []);
    setRoutes(snap.routes || []);
    setShapes(snap.shapes || []);
    setUtilities(snap.utilities || []);
  };
  const doUndo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const current = snapshotState();
    const prev = undoStack.current.pop();
    redoStack.current.push(current);
    applySnapshot(prev);
  }, [snapshotState]);
  
  const doRedo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    const current = snapshotState();
    const next = redoStack.current.pop();
    undoStack.current.push(current);
    applySnapshot(next);
  }, [snapshotState]);

  useEffect(() => {
    if (!registerExport) return;
    registerExport(exportAsPng);
    return () => registerExport(null);
  }, [registerExport, lines, players, routes]);

  // Routes: stored in image-space as [x,y,...]
  
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  const isRouteDrawing = useRef(false);
  const routeDrawingPlayerId = useRef(null);
  const routeDrawingId = useRef(null);



  // clear selection whenever we leave the move tool
  useEffect(() => {
    if (activeTool !== 'move') setSelectedPlayer(null);
  }, [activeTool]);

  useEffect(() => {
    const container = stageContainerRef.current;
    if (!container) return undefined;

    const updateStageSize = () => {
      const { width, height } = container.getBoundingClientRect();
      setStageSize({
        width: Math.max(1, Math.floor(width)),
        height: Math.max(1, Math.floor(height)),
      });
    };

    updateStageSize();

    const resizeObserver = new ResizeObserver(updateStageSize);
    resizeObserver.observe(container);
    window.addEventListener("resize", updateStageSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateStageSize);
    };
  }, []);

  useEffect(() => {
    // push undo snapshot, then clear
    pushUndo();
    setLines([]); // clear drawings when parent increments clearDrawings
    setRoutes([]);
    setShapes([]);
    setUtilities([]);
  }, [clearDrawings, pushUndo]);

  // Export stage as PNG (toolbar is outside the Konva Stage, so it won't be captured)
  const exportAsPng = (options = {}) => {
    const filename = options.filename || `tacmaps-${Date.now()}.png`;
    const pixelRatio = options.pixelRatio || 2; // increase for higher-res
    if (!stageRef.current) {
      console.warn("Stage ref not set; cannot export");
      return;
    }
    try {
      const dataUrl = stageRef.current.toDataURL({ mimeType: "image/png", pixelRatio });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to export image:", err);
    }
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = (e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        doUndo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        doRedo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [doRedo, doUndo]);

  // Register undo/redo handlers with parent (so toolbar can trigger them)
  useEffect(() => {
    if (registerUndo) registerUndo(doUndo);
    if (registerRedo) registerRedo(doRedo);
    return () => {
      if (registerUndo) registerUndo(null);
      if (registerRedo) registerRedo(null);
    };
  }, [registerUndo, registerRedo, doUndo, doRedo]);

  // Playback state & helpers
  const rafRef = useRef(null);
  const isPlayingRef = useRef(false);
  const playbackStateRef = useRef({ lastTs: 0 });

  const computeRouteMeta = (pts) => {
    const segs = [];
    let total = 0;
    for (let i = 0; i < pts.length - 2; i += 2) {
      const ax = pts[i];
      const ay = pts[i + 1];
      const bx = pts[i + 2];
      const by = pts[i + 3];
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.sqrt(dx * dx + dy * dy);
      segs.push({ ax, ay, bx, by, len });
      total += len;
    }
    return { segs, total };
  };

  const getPosAtDistance = (meta, d) => {
    if (!meta || meta.total <= 0) return { x: 0, y: 0, angle: 0 };
    let remaining = d;
    for (const s of meta.segs) {
      if (remaining <= s.len) {
        const t = s.len === 0 ? 0 : remaining / s.len;
        const x = linearInterp(s.ax, s.bx, t);
        const y = linearInterp(s.ay, s.by, t);
        const angle = (Math.atan2(s.by - s.ay, s.bx - s.ax) * 180) / Math.PI;
        return { x, y, angle };
      }
      remaining -= s.len;
    }
    const last = meta.segs[meta.segs.length - 1];
    return { x: last.bx, y: last.by, angle: (Math.atan2(last.by - last.ay, last.bx - last.ax) * 180) / Math.PI };
  };

  const startPlayback = () => {
    // snapshot before moving players so undo can restore
    pushUndo();
    isPlayingRef.current = true;
    playbackStateRef.current = { lastTs: performance.now() };

    // build per-player progress map
    const progress = {};
    for (const route of routes) {
      if (!route.points || route.points.length < 4) continue;
      const meta = computeRouteMeta(route.points);
      progress[route.playerId] = { meta, distance: 0, finished: false };
    }

    const BASE_SPEED = 200;

    const tick = (ts) => {
      if (!isPlayingRef.current) return;
      const last = playbackStateRef.current.lastTs || ts;
      const dt = (ts - last) / 1000;
      playbackStateRef.current.lastTs = ts;
      const speed = BASE_SPEED;

      let anyChange = false;
      const newPlayers = playersRef.current.map((p) => {
        const prog = progress[p.id];
        if (!prog || prog.finished) return p;
        prog.distance += speed * dt;
        if (prog.distance >= prog.meta.total) {
          prog.distance = prog.meta.total;
          prog.finished = true;
        }
        const pos = getPosAtDistance(prog.meta, prog.distance);
        anyChange = true;
        return { ...p, x: pos.x, y: pos.y, rotation: pos.angle };
      });

      if (anyChange) setPlayers(newPlayers);

      const allDone = Object.values(progress).every((p) => p.finished);
      if (allDone) {
        isPlayingRef.current = false;
        rafRef.current = null;
        // inform parent that playback completed so UI can update (toggle play->pause)
        if (typeof onPlaybackFinished === 'function') onPlaybackFinished();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  };

  const pausePlayback = () => {
    isPlayingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const stopPlayback = () => {
    pausePlayback();
    // leave players where they are; if desired, could reset to start
  };

  useEffect(() => {
    if (!playCommand || !playCommand.type) return;
    if (playCommand.type === "play") startPlayback();
    if (playCommand.type === "pause") pausePlayback();
    if (playCommand.type === "stop") stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playCommand, routes]);

  // cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // helper math
  const distSq = (ax, ay, bx, by) => {
    const dx = ax - bx;
    const dy = ay - by;
    return dx * dx + dy * dy;
  };

  /**
   * Linearly interpolates between two numeric values.
   *
   * @param {number} a - Start value.
   * @param {number} b - End value.
   * @param {number} t - Interpolation factor; 0 yields `a`, 1 yields `b`. Values outside [0, 1] produce extrapolation.
   * @returns {number} The interpolated value: a + (b - a) * t.
   *
   * @example
   * // returns 5 (midpoint between 0 and 10)
   * linearInterp(0, 10, 0.5);
   */
  const linearInterp = (a, b, t) => a + (b - a) * t;

  // Helper to check if a point is inside a rectangle
  // const isPointInRect = (px, py, x, y, width, height) => {
  //   return px >= x && px <= x + width && py >= y && py <= y + height;
  // };

  // Helper to check if a point is inside a circle
  // const isPointInCircle = (px, py, cx, cy, radius) => {
  //   const dx = px - cx;
  //   const dy = py - cy;
  //   return dx * dx + dy * dy <= radius * radius;
  // };

  // solve quadratic for segment-circle intersections; returns array of t values in [0,1]
  function segmentCircleTs(ax, ay, bx, by, cx, cy, r) {
    const dx = bx - ax;
    const dy = by - ay;
    const fx = ax - cx;
    const fy = ay - cy;

    const a = dx * dx + dy * dy;
    const b = 2 * (dx * fx + dy * fy);
    const c = fx * fx + fy * fy - r * r;

    const disc = b * b - 4 * a * c;
    if (disc < 0 || Math.abs(a) < 1e-12) return [];
    const sqrtD = Math.sqrt(disc);
    const t1 = (-b - sqrtD) / (2 * a);
    const t2 = (-b + sqrtD) / (2 * a);
    const out = [];
    if (t1 >= 0 && t1 <= 1) out.push(t1);
    if (t2 >= 0 && t2 <= 1) out.push(t2);
    out.sort((x, y) => x - y);
    return out;
  }

  // remove segments of lines that intersect the eraser circle centered at imgPoint (image-space coords)
  const eraseAt = (imgPoint) => {
    const radiusImg = ERASER_RADIUS / Math.max(1e-6, scale);
    const cx = imgPoint.x;
    const cy = imgPoint.y;

    // Remove whole shapes that intersect the eraser
    setShapes((prev) => prev.filter((shape) => {
      if (shape.type === "square") {
        const x = Math.min(shape.startX, shape.endX);
        const y = Math.min(shape.startY, shape.endY);
        const width = Math.abs(shape.endX - shape.startX);
        const height = Math.abs(shape.endY - shape.startY);
        
        // Check if eraser circle intersects the rectangle
        const closest = {
          x: Math.max(x, Math.min(cx, x + width)),
          y: Math.max(y, Math.min(cy, y + height)),
        };
        const dx = cx - closest.x;
        const dy = cy - closest.y;
        return !(dx * dx + dy * dy <= radiusImg * radiusImg);
      } else if (shape.type === "circle") {
        const centerX = (shape.startX + shape.endX) / 2;
        const centerY = (shape.startY + shape.endY) / 2;
        const radius = Math.sqrt((shape.endX - shape.startX) ** 2 + (shape.endY - shape.startY) ** 2) / 2;
        const dx = cx - centerX;
        const dy = cy - centerY;
        return !(dx * dx + dy * dy <= (radius + radiusImg) ** 2);
      } else if (shape.type === "line") {
        // Check if eraser circle intersects the line
        const ts = segmentCircleTs(shape.startX, shape.startY, shape.endX, shape.endY, cx, cy, radiusImg);
        return ts.length === 0;
      }
      return true;
    }));

    // Remove whole routes that intersect the eraser (any vertex within radius or any segment crossing the circle)
    setRoutes((prev) => prev.filter((route) => {
      const pts = route.points || [];
      if (pts.length < 2) return true; // keep invalid/empty routes
      // check vertices
      for (let i = 0; i < pts.length; i += 2) {
        const dx = pts[i] - cx;
        const dy = pts[i + 1] - cy;
        if (dx * dx + dy * dy <= radiusImg * radiusImg) return false; // touched -> remove route
      }
      // check segments
      const n = pts.length / 2;
      for (let i = 0; i < n - 1; i++) {
        const ax = pts[2 * i];
        const ay = pts[2 * i + 1];
        const bx = pts[2 * (i + 1)];
        const by = pts[2 * (i + 1) + 1];
        const ts = segmentCircleTs(ax, ay, bx, by, cx, cy, radiusImg);
        if (ts.length > 0) return false; // touched -> remove route
      }
      return true; // keep
    }));

    setLines((prev) => {
      const result = [];
      for (const line of prev) {
        const pts = line.points;
        if (!pts || pts.length < 4) continue;

        let current = []; // accumulating outside points for current kept segment

        const pushSegmentIfValid = () => {
          if (current.length >= 4) {
            result.push({ ...line, points: current.slice() });
          }
        };

        const n = pts.length / 2;
        for (let i = 0; i < n - 1; i++) {
          const ax = pts[2 * i];
          const ay = pts[2 * i + 1];
          const bx = pts[2 * (i + 1)];
          const by = pts[2 * (i + 1) + 1];

          const da = Math.sqrt(distSq(ax, ay, cx, cy));
          const db = Math.sqrt(distSq(bx, by, cx, cy));

          // both outside
          if (da > radiusImg && db > radiusImg) {
            const ts = segmentCircleTs(ax, ay, bx, by, cx, cy, radiusImg);
            if (ts.length === 0) {
              // keep A
              if (current.length === 0) {
                current.push(ax, ay);
              } else {
                // avoid duplicating consecutive points
                const lastIdx = current.length - 2;
                const lx = current[lastIdx];
                const ly = current[lastIdx + 1];
                if (Math.abs(lx - ax) > 1e-6 || Math.abs(ly - ay) > 1e-6) current.push(ax, ay);
              }
            } else if (ts.length === 1) {
              // rare: tangent - treat like no intersection
              if (current.length === 0) current.push(ax, ay);
            } else {
              // segment passes through: ts[0]=enter, ts[1]=exit
              const tEnter = ts[0];
              const tExit = ts[1];
              const pEnterX = linearInterp(ax, bx, tEnter);
              const pEnterY = linearInterp(ay, by, tEnter);
              const pExitX = linearInterp(ax, bx, tExit);
              const pExitY = linearInterp(ay, by, tExit);

              if (current.length === 0) current.push(ax, ay);
              current.push(pEnterX, pEnterY);
              pushSegmentIfValid();
              current = [pExitX, pExitY];
            }
          } else if (da > radiusImg && db <= radiusImg) {
            // outside -> inside: find entry point
            const ts = segmentCircleTs(ax, ay, bx, by, cx, cy, radiusImg);
            const t = ts.find((t) => t >= 0 && t <= 1) ?? 0;
            const pX = linearInterp(ax, bx, t);
            const pY = linearInterp(ay, by, t);
            if (current.length === 0) current.push(ax, ay);
            current.push(pX, pY);
            pushSegmentIfValid();
            current = [];
          } else if (da <= radiusImg && db > radiusImg) {
            // inside -> outside: find exit point
            const ts = segmentCircleTs(ax, ay, bx, by, cx, cy, radiusImg);
            const t = ts.find((t) => t >= 0 && t <= 1) ?? 1;
            const pX = linearInterp(ax, bx, t);
            const pY = linearInterp(ay, by, t);
            current = [pX, pY];
            // note: do not push yet; continue accumulating
          } else {
            // both inside -> do nothing
          }
        }

        // after loop, handle last point: include last if it's outside
        const lastX = pts[pts.length - 2];
        const lastY = pts[pts.length - 1];
        const dlast = Math.sqrt(distSq(lastX, lastY, cx, cy));
        if (dlast > radiusImg) {
          if (current.length === 0) {
            current.push(lastX, lastY);
          } else {
            const lastIdx = current.length - 2;
            const lx = current[lastIdx];
            const ly = current[lastIdx + 1];
            if (Math.abs(lx - lastX) > 1e-6 || Math.abs(ly - lastY) > 1e-6) current.push(lastX, lastY);
          }
        }

        // push remaining
        pushSegmentIfValid();
      }
      return result;
    });
  };

  // ensure cursor class removed on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove("eraser-active");
    };
  }, []);

  // Helper to detect if the event target (or any parent) is a player
  const isEventOnPlayer = (target) => {
    try {
      let node = target;
      while (node && node.getAttr) {
        const name = node.getAttr("name");
        if (name && typeof name === "string" && name.startsWith("player-")) return true;
        if (node.draggable && node.draggable()) return true;
        node = node.getParent && node.getParent();
      }
    } catch (err) {
      // ignore
    }
    return false;
  };

  // Find player id from a DOM/Konva target by walking up parents; returns number or null
  const getPlayerIdFromTarget = (target) => {
    try {
      let node = target;
      while (node && node.getAttr) {
        const name = node.getAttr("name");
        if (name && typeof name === "string" && name.startsWith("player-")) {
          const id = parseInt(name.replace("player-", ""), 10);
          return Number.isNaN(id) ? null : id;
        }
        node = node.getParent && node.getParent();
      }
    } catch (err) {
      // ignore
    }
    return null;
  };

  const handleMouseDown = (e) => {
    const button = e.evt && e.evt.button;

    // Right-click starts erasing while held; also support the Eraser tool (left-click)
    if ((button === 2 && activeTool === 'draw')|| activeTool === 'eraser') {
      if (button === 2 ) e.evt.preventDefault();
      // snapshot for undo
      pushUndo();
      isErasing.current = true;
      const pos = e.target.getStage().getPointerPosition();
      setEraserPos(pos);
      eraseAt(stageToImage(pos));
      document.body.classList.add("eraser-active");
      return;
    }

    const target = e.target;
    const playerId = getPlayerIdFromTarget(target);

    // If user clicks a player while the Draw tool is active, start a route attached to that player
    if (playerId && activeTool === "draw") {
      e.evt.preventDefault();
      pushUndo();
      isRouteDrawing.current = true;
      routeDrawingPlayerId.current = playerId;
      // Anchor the route start to the player's current image-space position to avoid mismatch/jumps
      const player = playersRef.current.find((p) => p.id === playerId);
      const startX = player ? player.x : (() => {
        const pos = e.target.getStage().getPointerPosition();
        return stageToImage(pos).x;
      })();
      const startY = player ? player.y : (() => {
        const pos = e.target.getStage().getPointerPosition();
        return stageToImage(pos).y;
      })();
      const rid = `route-${Date.now()}`;
      routeDrawingId.current = rid;
      setRoutes((prev) => [...prev, { id: rid, playerId, points: [startX, startY] }]);
      return;
    }

    // If Move tool is active and a player was clicked, select/deselect the player and prevent drawing
    if (playerId && activeTool === "move") {
      // select/deselect player on click; do not prevent default or return so dragging can start immediately
      setSelectedPlayer((prev) => (prev === playerId ? null : playerId));
    }

    // If Move tool is active and user clicked empty stage, clear selection
    if (!playerId && activeTool === "move") {
      console.log(selectedPlayer);
      setSelectedPlayer(null);
      return;
    }

    // If shapes tool is active and a shape is selected, start drawing shape
    if (activeTool === "shapes" && selectedShape) {
      e.evt.preventDefault();
      pushUndo();
      isDrawingShape.current = true;
      const pos = e.target.getStage().getPointerPosition();
      const imgPos = stageToImage(pos);
      shapeDrawingStart.current = { x: imgPos.x, y: imgPos.y };
      return;
    }

    // otherwise, ignore starting freehand if interacting with a player
    if (isEventOnPlayer(target)) return; // don't start drawing when interacting with a player
    if (activeTool !== "draw") return; // only draw when draw tool is active

    // snapshot at the start of drawing
    pushUndo();
    isDrawing.current = true;
    const pos = e.target.getStage().getPointerPosition();
    const imgPos = stageToImage(pos);
    setLines((prev) => [...prev, { points: [imgPos.x, imgPos.y], color: drawingColor }]);
  }; 

  const handleMouseMove = (e) => {
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();

    if (isErasing.current) {
      setEraserPos(point);
      eraseAt(stageToImage(point));
      return;
    }

    // If drawing a shape, update preview
    if (isDrawingShape.current && shapeDrawingStart.current) {
      const imgPoint = stageToImage(point);
      setShapes((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.isPreview) {
          return prev.slice(0, -1).concat({
            type: selectedShape,
            color: drawingColor,
            startX: shapeDrawingStart.current.x,
            startY: shapeDrawingStart.current.y,
            endX: imgPoint.x,
            endY: imgPoint.y,
            isPreview: true,
          });
        } else {
          return prev.concat({
            type: selectedShape,
            color: drawingColor,
            startX: shapeDrawingStart.current.x,
            startY: shapeDrawingStart.current.y,
            endX: imgPoint.x,
            endY: imgPoint.y,
            isPreview: true,
          });
        }
      });
      return;
    }

    // If drawing a route from a player, add densified points to the current route
    if (isRouteDrawing.current) {
      const imgPoint = stageToImage(point);
      setRoutes((prev) => {
        if (prev.length === 0) return prev;
        const lastIndex = prev.length - 1;
        const last = prev[lastIndex];
        if (last.playerId !== routeDrawingPlayerId.current) return prev;
        const pts = last.points;
        const lastX = pts[pts.length - 2];
        const lastY = pts[pts.length - 1];
        const dx = imgPoint.x - lastX;
        const dy = imgPoint.y - lastY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const steps = Math.max(1, Math.ceil(dist / DENSIFY_THRESHOLD));

        const newPoints = [];
        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          newPoints.push(linearInterp(lastX, imgPoint.x, t), linearInterp(lastY, imgPoint.y, t));
        }

        const updatedLast = { ...last, points: last.points.concat(newPoints) };
        return prev.slice(0, lastIndex).concat(updatedLast);
      });
      return;
    }

    if (!isDrawing.current) return;
    const imgPoint = stageToImage(point);

    setLines((prev) => {
      if (prev.length === 0) return prev;
      const lastIndex = prev.length - 1;
      const last = prev[lastIndex];
      const pts = last.points;
      const lastX = pts[pts.length - 2];
      const lastY = pts[pts.length - 1];
      const dx = imgPoint.x - lastX;
      const dy = imgPoint.y - lastY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.max(1, Math.ceil(dist / DENSIFY_THRESHOLD));

      const newPoints = [];
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        newPoints.push(linearInterp(lastX, imgPoint.x, t), linearInterp(lastY, imgPoint.y, t));
      }

      const updatedLast = { ...last, points: last.points.concat(newPoints) };
      return prev.slice(0, lastIndex).concat(updatedLast);
    });
  };

  const handleMouseUp = (e) => {
    // stop erasing on mouse up
    if (isErasing.current) {
      isErasing.current = false;
      setEraserPos(null);
      document.body.classList.remove("eraser-active");
    }

    // finish shape drawing if active
    if (isDrawingShape.current) {
      setShapes((prev) => prev.map((s) => ({ ...s, isPreview: false })));
      isDrawingShape.current = false;
      shapeDrawingStart.current = null;
    }

    // finish route drawing if active
    if (isRouteDrawing.current) {
      // ensure player's position is anchored to the start point of the route to avoid jumping
      const rid = routeDrawingId.current;
      const route = routesRef.current.find((r) => r.id === rid) || routesRef.current[routesRef.current.length - 1];
      if (route && route.points && route.points.length >= 2) {
        const sx = route.points[0];
        const sy = route.points[1];
        setPlayers((prev) => prev.map((p) => (p.id === route.playerId ? { ...p, x: sx, y: sy } : p)));
      }

      isRouteDrawing.current = false;
      routeDrawingPlayerId.current = null;
      routeDrawingId.current = null;
    }

    isDrawing.current = false;
  };

  
  const handlePlayerMove = (id, newX, newY) => {
    // newX/newY are stage coordinates; convert back to image-space
    const img = stageToImage({ x: newX, y: newY });
    setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, x: img.x, y: img.y } : p)));
  };

  const handlePlayerDragStart = (id) => {
    // snapshot before a drag so undo restores pre-drag position
    pushUndo();
  };

  const handlePlayerDragEnd = (id, stageX, stageY) => {
    // ensure final position recorded (already updated via onDragMove), but you could push final state if desired
    // For now we don't push another snapshot here so undo is a single step back to pre-drag
  };

  const handleUtilityMove = (id, newX, newY) => {
    const img = stageToImage({ x: newX, y: newY });
    setUtilities((prev) => prev.map((u) => (u.id === id ? { ...u, x: img.x, y: img.y } : u)));
  };

  const handleUtilityDragStart = (id) => {
    pushUndo();
  };

  const handleUtilityDragEnd = (id, stageX, stageY) => {
    // No additional snapshots on drag end
  };

  const handleUtilityContextMenu = (id) => {
    // Delete utility on right-click
    setUtilities((prev) => prev.filter((u) => u.id !== id));
  };

  const handleBadgeContext = (id, evt) => {
    // Safely handle Konva event (`evt.evt`) or a DOM event; guard against undefined
    try {
      if (evt && evt.evt && typeof evt.evt.preventDefault === "function") evt.evt.preventDefault();
    } catch (err) {
      // ignore
    }
    const clientX = (evt && evt.evt && evt.evt.clientX) || (evt && evt.clientX) || (window.event && window.event.clientX) || 0;
    const clientY = (evt && evt.evt && evt.evt.clientY) || (evt && evt.clientY) || (window.event && window.event.clientY) || 0;
    setBadgePopAnchor({ x: clientX, y: clientY });
    setBadgeTargetId(id);
    setBadgePopOpen(true);
  };

  const handleBadgeSelect = (num) => {
    if (!badgeTargetId) return;
    pushUndo();
    setUtilities((prev) => prev.map((u) => (u.id === badgeTargetId ? { ...u, number: num } : u)));
    setBadgePopOpen(false);
    setBadgeTargetId(null);
  };

  const handleBorderColorChange = (color) => {
    if (!badgeTargetId) return;
    setUtilities((prev) => prev.map((u) => (u.id === badgeTargetId ? { ...u, borderColor: color } : u)));
  };

  const handlePlayerColorChange = (color) => {
    if (!popoutPlayerId) return;
    setPlayers((prev) => prev.map((p) => (p.id === popoutPlayerId ? { ...p, strokeColor: color } : p)));
  };

  const handleStageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleStageDrop = useCallback((e) => {
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData("application/json");
      if (!data) return;

      const payload = JSON.parse(data);
      if (payload.type !== "utility") return;

      // Get the stage container's position
      const stageContainer = stageRef.current.container();
      const rect = stageContainer.getBoundingClientRect();
      const stageX = e.clientX - rect.left;
      const stageY = e.clientY - rect.top;

      // Convert to image-space
      const imgCoords = stageToImage({ x: stageX, y: stageY });

      // Create new utility with typeKey and sequential number (1-4)
      const newId = Math.max(0, ...utilities.map((u) => u.id || 0)) + 1;
      const typeKey = (payload.src || "").split("/").pop().split(".")[0].toLowerCase();
      const count = utilities.filter((u) => u.typeKey === typeKey).length;
      const number = (count % 4) + 1;
      const newUtility = {
        id: newId,
        x: imgCoords.x,
        y: imgCoords.y,
        src: payload.src,
        typeKey,
        number,
      };

      pushUndo();
      setUtilities((prev) => [...prev, newUtility]);
    } catch (error) {
      console.error("Error handling utility drop:", error);
    }
  }, [stageToImage, utilities, pushUndo]);

  // Attach drag-drop handlers on mount
  useEffect(() => {
    if (!stageRef.current) return;
    
    const container = stageRef.current.container();
    if (!container) return;

    container.addEventListener("dragover", handleStageDragOver);
    container.addEventListener("drop", handleStageDrop);

    return () => {
      container.removeEventListener("dragover", handleStageDragOver);
      container.removeEventListener("drop", handleStageDrop);
    };
  }, [utilities, stageToImage, handleStageDrop]);

  useEffect(() => {
    if (!calloutImageVisible) return;
    if (!currentMap || !currentFloor) {
      setCalloutImageVisible(false);
      return;
    }

    const nextSrc = getCurrentFloorCalloutSrc(currentMap, currentFloor);
    setCalloutImageSrc(nextSrc);
  }, [currentFloor, currentMap, calloutImageVisible]);

  useEffect(() => {
    if (!camImageVisible) return;
    if (!currentMap || !currentFloor) {
      setCamImageVisible(false);
      return;
    }

    const nextSrc = getCurrentFloorLayerSrc(currentMap, currentFloor, 'cams');
    setCamImageSrc(nextSrc);
  }, [currentFloor, currentMap, camImageVisible]);

  return (
    <div className="whiteboard-wrap">
      {/* Floor Toolbar at the top */}
      {currentMap && currentMap.floors && (
        <FloorToolbar
          floors={currentMap.floors}
          selectedFloorId={selectedFloorId}
          onSelectFloor={handleSelectFloor}
        />
      )}

      <div className="whiteboard-stage" ref={stageContainerRef}>
        {stageSize.width > 0 && stageSize.height > 0 && (
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            onMouseDown={handleMouseDown}
            onMousemove={handleMouseMove}
            onMouseup={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            onContextMenu={(e) => e.evt.preventDefault()}
            style={{ background: "#111", display: "block" }}
          >
            <Layer>
              {/* Background image layer (behind the wireframe) */}
              {backgroundImageVisible && backgroundImage && (
                <KonvaImage
                  image={backgroundImage}
                  x={offsetX}
                  y={offsetY}
                  width={imgDisplayW}
                  height={imgDisplayH}
                  opacity={1}
                  listening={false}
                />
              )}
              {/* Wireframe image always rendered on top, regardless of background visibility */}
              {mapImage && (
                <KonvaImage
                  image={mapImage}
                  x={offsetX}
                  y={offsetY}
                  width={imgDisplayW}
                  height={imgDisplayH}
                  opacity={1}
                  listening={false}
                />
              )}
              {calloutImageVisible && calloutImage && (
                <KonvaImage
                  image={calloutImage}
                  x={offsetX}
                  y={offsetY}
                  width={imgDisplayW}
                  height={imgDisplayH}
                  opacity={1}
                  listening={false}
                />
              )}
              {camImageVisible && camImage && (
                <KonvaImage
                  image={camImage}
                  x={offsetX}
                  y={offsetY}
                  width={imgDisplayW}
                  height={imgDisplayH}
                  opacity={1}
                  listening={false}
                />
              )}
              {siteImageVisible && siteImage && (
                <KonvaImage
                  image={siteImage}
                  x={offsetX}
                  y={offsetY}
                  width={imgDisplayW}
                  height={imgDisplayH}
                  opacity={1}
                  listening={false}
                />
              )}
        {/* <MapImage src={currentMap ? `/maps/R6S/Chalet/Hacienda_base.png` : null} stageWidth={stageSize.width} stageHeight={stageSize.height} /> */}
            {/* routes (drawn from players) */}
            {routes.map((route) => {
              const player = players.find((p) => p.id === route.playerId);
              const color = player && player.img && player.img.toLowerCase().includes("red") ? "#f55" : "#0af";
              return (
                <>
                  <Line
                    key={route.id}
                    points={imageToStagePoints(route.points)}
                    stroke={color}
                    strokeWidth={Math.max(2, 3 * scale)}
                    tension={0.4}
                    dash={[8, 4]}
                    lineCap="round"
                    listening={false}
                  />
                  {route.points.length >= 2 && (
                    <Circle
                      key={`${route.id}-start`}
                      x={route.points[0] * scale + offsetX}
                      y={route.points[1] * scale + offsetY}
                      radius={6}
                      fill={color}
                      listening={false}
                    />
                  )}
                </>
              );
            })}

            {/* shapes (square, circle, line) */}
            {shapes.map((shape, i) => {
              if (shape.type === "square") {
                const x = Math.min(shape.startX, shape.endX);
                const y = Math.min(shape.startY, shape.endY);
                const width = Math.abs(shape.endX - shape.startX);
                const height = Math.abs(shape.endY - shape.startY);
                return (
                  <Rect
                    key={`shape-${i}`}
                    x={x * scale + offsetX}
                    y={y * scale + offsetY}
                    width={width * scale}
                    height={height * scale}
                    stroke={shape.color || "red"}
                    strokeWidth={Math.max(2, 3 * scale)}
                    dash={shape.isPreview ? [8, 4] : undefined}
                    listening={false}
                  />
                );
              } else if (shape.type === "circle") {
                const centerX = (shape.startX + shape.endX) / 2;
                const centerY = (shape.startY + shape.endY) / 2;
                const radius = Math.sqrt((shape.endX - shape.startX) ** 2 + (shape.endY - shape.startY) ** 2) / 2;
                return (
                  <Circle
                    key={`shape-${i}`}
                    x={centerX * scale + offsetX}
                    y={centerY * scale + offsetY}
                    radius={radius * scale}
                    stroke={shape.color || "red"}
                    strokeWidth={Math.max(2, 3 * scale)}
                    dash={shape.isPreview ? [8, 4] : undefined}
                    listening={false}
                  />
                );
              } else if (shape.type === "line") {
                return (
                  <Line
                    key={`shape-${i}`}
                    points={[
                      shape.startX * scale + offsetX,
                      shape.startY * scale + offsetY,
                      shape.endX * scale + offsetX,
                      shape.endY * scale + offsetY,
                    ]}
                    stroke={shape.color || "red"}
                    strokeWidth={Math.max(2, 3 * scale)}
                    dash={shape.isPreview ? [8, 4] : undefined}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                  />
                );
              }
              return null;
            })}

            {lines.map((line, i) => (
                <Line
                    key={i}
                    points={imageToStagePoints(line.points)}
                    stroke={line.color || "red"}
                    strokeWidth={Math.max(1, 3 * scale)}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation="source-over"
                />
            ))}
            {players.map((player) => (
            <>
            <PlayerArrow
                key={`p-${player.id}`}
                id={player.id}
                name={`player-${player.id}`}
                x={player.x * scale + offsetX}
                y={player.y * scale + offsetY}
                rotation={player.rotation}
                onDragStart={handlePlayerDragStart}
                onDragMove={handlePlayerMove}
                onDragEnd={handlePlayerDragEnd}
                src={player.img}
                size={64 * scale}
                strokeColor={player.strokeColor || 'red'}
                // make arrows draggable for Move tool so users can click+drag immediately
                draggable={activeTool === 'move'}
                onContextMenu={(id, evt) => {
                  // only open popout when right-clicking a player in Move mode and game is r6s
                  if (activeTool === 'move' && game === 'r6s') {
                    evt.evt && evt.evt.preventDefault && evt.evt.preventDefault();
                    const clientX = evt.evt.clientX;
                    const clientY = evt.evt.clientY;
                    setPopoutAnchor({ x: clientX, y: clientY });
                    setPopoutPlayerId(id);
                    setPopoutOpen(true);
                  }
                }}
            />
            </>
            ))}

            {/* utilities (draggable from popup) */}
            {utilities.map((utility) => (
              <UtilityIcon
                key={`u-${utility.id}`}
                id={utility.id}
                x={utility.x * scale + offsetX}
                y={utility.y * scale + offsetY}
                onDragStart={handleUtilityDragStart}
                onDragMove={handleUtilityMove}
                onDragEnd={handleUtilityDragEnd}
                src={utility.src}
                size={40 * scale}
                draggable={activeTool === 'move'}
                onContextMenu={(id) => handleUtilityContextMenu(id)}
                number={utility.number}
                onBadgeContext={handleBadgeContext}
                borderColor={utility.borderColor || '#ff0000'}
              />
            ))}



            {/* eraser indicator (visible while right-button erasing) */}
            {eraserPos && (
              <Circle
                x={eraserPos.x}
                y={eraserPos.y}
                radius={ERASER_RADIUS}
                stroke="#fff"
                strokeWidth={2}
                opacity={0.7}
                listening={false}
                dash={[6, 6]}
              />
            )}
            </Layer>
          </Stage>
        )}
      </div>

    {/* popout icon selector (rendered outside Konva as an overlay) */}
    <PopoutIconGrid
      visible={popoutOpen}
      anchor={popoutAnchor}
      playerId={popoutPlayerId}
      onClose={() => setPopoutOpen(false)}
      currentColor={players.find((p) => p.id === popoutPlayerId)?.strokeColor || 'red'}
      onColorChange={handlePlayerColorChange}
      onSelect={(src) => {
        // apply icon change to selected player and push undo
        if (!popoutPlayerId) return;
        pushUndo();
        setPlayers((prev) => prev.map((p) => (p.id === popoutPlayerId ? { ...p, img: src } : p)));
        setPopoutOpen(false);
      }}
    />
    {/* badge number popout rendered outside Konva Stage to avoid ReactKonva host conflicts */}
    <UtilityIconPopoutGrid
      visible={badgePopOpen}
      anchor={badgePopAnchor}
      currentNumber={utilities.find((u) => u.id === badgeTargetId)?.number || 1}
      onSelect={handleBadgeSelect}
      onClose={() => setBadgePopOpen(false)}
      currentColor={utilities.find((u) => u.id === badgeTargetId)?.borderColor || '#ff0000'}
      onColorChange={handleBorderColorChange}
    />
  </div>
);
};

export default R6sWhiteboard;
